import React, { Fragment, useState, useEffect, useRef } from "react";
import Header from "../../components/Header";
import Banner from "../../components/Banner";
import PrimeLayout from "../../components/PrimeLayout";
import { Grid } from "@mui/material";
import StyledSelect from "../../components/elements/StyledSelect";
import StyledButton from "../../components/elements/StyledButton";
import StyledTable from "../../components/StyledTable";
import { userRolesSuperAdmin, userRolesAdmin } from "../../constants/UserRoles";
import { userHeadCells } from "../../constants/Data";
import UserModal from "../../components/UserModal";
import Pagination from "../../components/elements/Pagination";
import { BackDrop } from "../../components/elements/Loader";
import { BiUserPlus } from "react-icons/bi";
import {
  getAllUsers,
  setActiveInactive,
  createUser,
  editUser,
  getAllModelNames,
  getAllUserGroups,
} from "../../services/api/users.api";
import {
  formatRolesFrontEndToBAckEnd,
  formatRolesBackEndToFrontEnd,
  formatEligibleModels,
} from "../../utils/filterRoles";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import PrimarySearchBar from "../../components/PrimarySearchBar";
import styled from "styled-components";

const TextFiled = styled.div`
  font-size: 16px;
  color: #707070;
  font-family: 'Open Sans';
`;

export default function Users() {
  const [loading, setLoading] = useState(true);
  const [reRender, setReRender] = useState(false);
  const [role, setRole] = useState("init");
  const [order, setOrder] = useState({ id: "name", sort: "asc" });
  const [users, setUsers] = useState({});
  const [openAction, setOpenAction] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [page, setPage] = useState(1);
  const [userRows, setUserRows] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState("init");
  const nameRef = useRef(name);

  const navigate = useNavigate();
  const getPage = (p) => {
    setPage(p);
  };
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const getModulesAll = async () => {
      try {
        const moduleNames = await getAllModelNames();
        const labels = moduleNames.map((d) => {
          return {
            template_id: d._id,
            label: d.name,
          };
        }).sort((a, b) => a.label.localeCompare(b.label));
        setModuleOptions([{ label: "Assign All" }, ...labels]);
      } catch (err) {
        if (err?.response?.data?.error.message)
          return toast.error(err.response.data.error.message, {
            theme: "colored",
          });
        toast.error(err.message, { theme: "colored" });
      }
    };
    getModulesAll();
    getUserGroups();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      if (user?.user_role !== "admin" && user?.user_role !== "super_admin") {
        toast.error("You do not have access to this page");
        return navigate("/new-upload");
      }
    }
  }, [user, navigate]);

  useEffect(() => {
      nameRef.current = name;
  }, [name]);

  const getUserGroups = async () => {
    try {
      const userGroups = await await getAllUserGroups(
        1,
        20000,
      );
      const labels = userGroups?.docs?.map((g) => {
        return {
          group_id: g._id,
          label: g.group_name,
        };
      }).sort((a, b) => a.label.localeCompare(b.label));
      setUserGroups([...labels]);
      setGroups(userGroups.docs);

    } catch (err) {
      if (err?.response?.data?.error.message)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  const toggleUser = async (data) => {
    try {
      const toggle = await setActiveInactive(data.id);
      const updateRow = userRows.map((row) => {
        if (row.id === data.id) {
          row.status = row.status ? false : true;
        }
        return row;
      });
      setUserRows([...updateRow]);
      if (toggle.is_active)
        return toast.success(`${data.name} is now active`, {
          theme: "colored",
        });
      if (!toggle.is_active)
        return toast.warning(`${data.name} is now inactive`, {
          theme: "colored",
        });
    } catch (err) {
      toast.error(err.message, {
        theme: "colored",
      });
    }
  };

  useEffect(() => {
    getUsers();
  }, [order, role, page, reRender, navigate]);

  const getUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAllUsers(
        page,
        order.id,
        order.sort,
        role === "All users" ? "all" : role,
        10,
        nameRef.current,
      );
      const rows = usersData.docs.map((user) => {
        let data = {};
        const groups = user.group.map(g => (
          {
            name: g.groupName,
            group_id: g.group_id
          } 
        ));
        data.id = user._id;
        data.name = user.name;
        data.user_role = formatRolesBackEndToFrontEnd(user.user_role);
        data.email = user.email;
        data.templates = user.templates;
        data.group_name =  groups ?? [];
        data.web_app_access = formatEligibleModels({
          web_portal_access: user.web_portal_access,
          web_map_access: user.web_map_access,
        });
        data.status = user.is_active;
        return data;
      });
      setUserRows([...rows]);
      setUsers(usersData);
      if(name !== "" && rows?.length === 0) {
        toast.warning("Sorry, no match found for the search criteria.", {
          theme: "colored",
        });
      }
      setTimeout(() => {
        setLoading(false);
      }, 800);
    } catch (err) {
      if (err.message === "No active account") {
        toast.error("Session Expired, Please Log back", {
          theme: "colored",
        });
        navigate("/");
      }
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const updateUser = async (e) => {
    try {
      const groups = e.user_group?.map(g => (
        {
          groupName: g.name,
          group_id: g.group_id
        } 
      ));
      
      e.user_role = formatRolesFrontEndToBAckEnd(e.user_role);
      e.templates =
        e.user_role === "admin" || e.user_role === "super_admin"
          ? []
          : e.templates;
      e.user_group = groups;
      await editUser(e);
      toast.success("User edited successfully", {
        theme: "colored",
      });
      setReRender(!reRender);
    } catch (err) {
      setReRender(!reRender);
      if (err.response.data.error.message)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  const handleEditUser = async (e) => {
    try {
      setSelectedUser(e);
      setOpenAction(true);
    } catch (err) {
      if (err.message === "No active account") {
        toast.error("Session Expired, Please Log back", {
          theme: "colored",
        });
        return navigate("/");
      }
      if (err.response.data.error.message)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  const handleCreateNewUser = async (e) => {
    try {
      const groups = e.user_group?.map(g => (
        {
          groupName: g.name,
          group_id: g.group_id
        } 
      ));
      
      await createUser({
        name: e.name,
        email: e.email,
        templates:
          e.user_role === "Admin" || e.user_role === "Super Admin"
            ? []
            : e.templates,
        user_role: formatRolesFrontEndToBAckEnd(e.user_role),
        group : groups
      });
      setReRender(!reRender);
      toast.success("User added successfully", {
        theme: "colored",
      });
    } catch (err) {
      toast.error(err);
      toast.error(err.response?.data?.error.message, {
        theme: "colored",
      });
    }
  };

  return (
    <Fragment>
      {isLoading ? (
        <BackDrop />
      ) : (
        <PrimeLayout title="Manage Users">
          <UserModal
            open={openAction}
            setOpen={setOpenAction}
            userData={selectedUser}
            handleEditUser={updateUser}
            moduleOptions={moduleOptions}
            groupOptions={userGroups}
            edit
            pageRerender={() => {
              setReRender(!reRender);
            }}
          />
          {/* Add User */}
          <UserModal
            open={openAddUser}
            setOpen={setOpenAddUser}
            handleCreateNewUser={handleCreateNewUser}
            moduleOptions={moduleOptions}
            groupOptions={userGroups}
            pageRerender={() => {
              setReRender(!reRender);
            }}
          />
          <Grid
            container
            spacing={3}
            justifyContent={"space-between"}
            alignItems={"center"}
            align={"right"}
          >
            <Grid item xs={12} md={3}>
              <StyledSelect
                label={"User Role"}
                defaultVal={"Select User Role"}
                options={
                  user?.user_role === "super_admin"
                    ? userRolesSuperAdmin
                    : userRolesAdmin
                }
                control={role}
                setControl={(role) => {
                  setPage(1);
                  setRole(role);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3} align={"left"}>
              <TextFiled>User Name</TextFiled>
              <PrimarySearchBar
                value={name === "init" ? "" : name}
                setControl={(val) => {
                  setName(val)
                }}
                handleSearchClick={() => {
                  setPage(1);
                  getUsers();
                }}
                placeholder="Search by User Name"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledButton
                variant={"outlined"}
                iconLeft={<BiUserPlus size={20} />}
                onClick={() => {
                  setOpenAddUser(true);
                }}
              >
                Add New User
              </StyledButton>
            </Grid>
          </Grid>
          <Grid xs={12}>
            <Fragment>
              <StyledTable
                headCells={userHeadCells}
                rows={userRows}
                order={order}
                setOrder={setOrder}
                slider={"status"}
                clickSwitch={toggleUser}
                editClick={handleEditUser}
                loading={loading}
                action
                edit
              />
              {users.totalPages > 1 && !loading && (
                <Pagination
                  count={users.totalPages}
                  getPage={getPage}
                  page={page}
                />
              )}
            </Fragment>
          </Grid>
        </PrimeLayout>
      )}
      <Footer />
    </Fragment>
  );
}
